import { DEFAULT_FILTERS_PAGE_SIZE } from '@/lib/config';
import type {
  AreaTypes,
  CaseLegislationTypeApiResponse,
  CourtsType,
  DecisionTypeInterface,
  JudgesType,
  LegislationTypeWithCountInterface,
  NatureType,
  ReleaseDateRangeInterface,
  SubAreaType,
  YearsRangeInterface,
} from '@/types/api-types';
import type { LegislationType } from '@/types/pdfTypes';
import ApiService, { type ApiResponse } from './api-services';

interface PaginationParams {
  page?: number | undefined;
  page_size?: number | undefined;
  search?: string | undefined;
  decision_types?: string | undefined;
}

export interface JurisdictionOption {
  id: string;
  name: string;
}

export type JurisdictionScope = 'legislation' | 'decisions';

export interface FilterDefaultsResponse {
  searchLegislation: boolean;
  searchDecisions: boolean;
  legislation: {
    activeOnly: boolean;
    effectiveAsOf: string;
    yearRange: YearsRangeInterface;
    legislationTypes: number[] | null;
    jurisdictions: string[] | null;
    // Single-sourced from DEFAULT_LEGISLATION_IN_FORCE_ONLY on the backend (#7623).
    // Read this instead of restating a frontend literal (#7652).
    legislationInForceOnly: boolean;
  };
  decisions: {
    areas: number[] | null;
    courts: number[] | null;
    judges: number[] | null;
    natures: number[] | null;
    subAreas: number[] | null;
    caseLegislationTypes: string[] | null;
    decisionTypes: string[] | null;
    releaseDateRange?: ReleaseDateRangeInterface | undefined;
    jurisdictions: string[] | null;
  };
}

class FiltersServices extends ApiService {
  constructor() {
    super('/filters');
  }

  getAllAreas(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<AreaTypes[]> {
    return this.getData('/areas', { params });
  }

  getAllCourts(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<CourtsType[]> {
    return this.getData('/courts', { params });
  }

  getAllJudges(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<JudgesType[]> {
    return this.getData('/judges', { params });
  }

  getAllNatures(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<NatureType[]> {
    return this.getData('/natures', { params });
  }

  getAllSubareas(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<SubAreaType[]> {
    return this.getData('/subareas', { params });
  }

  getAllLegislationTypes(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<LegislationType[]> {
    return this.getData('/legislation-types', { params });
  }

  getAllYearsRange(): ApiResponse<YearsRangeInterface> {
    return this.getData('/publication-dates');
  }

  getReleaseDatesRange(): ApiResponse<ReleaseDateRangeInterface> {
    return this.getData('/release-dates');
  }

  getAllDecisionKinds(
    params: PaginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE }
  ): ApiResponse<CaseLegislationTypeApiResponse[]> {
    return this.getData('/decision-kinds', { params });
  }

  getAllDecisionTypes(): ApiResponse<DecisionTypeInterface[]> {
    return this.getData('/decision-types');
  }

  getLegislationTypesWithCounts(): ApiResponse<
    LegislationTypeWithCountInterface[]
  > {
    return this.getData('/legislation-types-with-counts');
  }

  getDefaults(): ApiResponse<FilterDefaultsResponse> {
    return this.getData('/defaults');
  }

  getAllJurisdictions(
    scope?: JurisdictionScope
  ): ApiResponse<JurisdictionOption[]> {
    return this.getData(
      '/jurisdictions',
      scope ? { params: { scope } } : undefined
    );
  }
}

export default new FiltersServices();
